import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Scanner } from "@yudiel/react-qr-scanner";
import {
  MdQrCodeScanner,
  MdCheckCircle,
  MdError,
  MdInfo,
  MdClose,
  MdCameraAlt,
  MdUpload,
  MdRefresh,
} from "react-icons/md";
import { API_URL } from "../../constant";
import "./QrScanner.css";

const QrScanner = () => {
  const navigate = useNavigate();
  const [scanning, setScanning] = useState(true);
  const [scanResult, setScanResult] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showManualInput, setShowManualInput] = useState(false);
  const [manualCode, setManualCode] = useState("");
  const [scannerKey, setScannerKey] = useState(0);
  const [cameraError, setCameraError] = useState(false);
  const fileInputRef = useRef(null);
  const isProcessing = useRef(false);
  const scannerContainerRef = useRef(null);
  const [scannerDimensions, setScannerDimensions] = useState({ width: 0, height: 0 });
  
  const user = JSON.parse(localStorage.getItem("user") || "{}");

  useEffect(() => {
    const token = localStorage.getItem("auth_token");
    if (!token) {
      navigate("/login");
    }
  }, [navigate]);

  // Update scanner dimensions on resize
  useEffect(() => {
    const updateDimensions = () => {
      if (scannerContainerRef.current) {
        const { offsetWidth, offsetHeight } = scannerContainerRef.current;
        setScannerDimensions({
          width: offsetWidth,
          height: offsetHeight
        });
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    
    return () => {
      window.removeEventListener('resize', updateDimensions);
    };
  }, []);

  // Extract QR value from scanner result
  const extractQrValue = (decodedText) => {
    try {
      console.log("Raw decoded text:", decodedText);
      
      // If it's already a string, return it
      if (typeof decodedText === 'string') {
        return decodedText.trim();
      }
      
      // If it's an object with text/string property
      if (decodedText && typeof decodedText === 'object') {
        return decodedText.text || decodedText.data || JSON.stringify(decodedText);
      }
      
      return null;
    } catch (e) {
      console.error("Error extracting QR value:", e);
      return null;
    }
  };

  const extractTopCoinCardParams = (data) => {
    if (!data) return null;
    const str = String(data).trim();

    // Check if JSON
    if (str.startsWith("{") && str.endsWith("}")) {
      try {
        const obj = JSON.parse(str);
        const card = obj.card || obj.card_number || obj.cardNumber;
        const serial = obj.serial || obj.serial_number || obj.serialNumber || "";
        if (card) return { card: String(card).trim(), serial: String(serial).trim() };
      } catch (e) {}
    }

    // Check if URL or contains 'card='
    if (str.includes("card=") || str.includes("claim-card")) {
      try {
        let urlObj;
        if (str.startsWith("http://") || str.startsWith("https://")) {
          urlObj = new URL(str);
        } else {
          urlObj = new URL(str, "https://topcoinuk.com");
        }
        const card = urlObj.searchParams.get("card") || urlObj.searchParams.get("card_number");
        const serial = urlObj.searchParams.get("serial") || urlObj.searchParams.get("serial_number") || "";
        if (card) return { card: card.trim(), serial: serial.trim() };
      } catch (e) {
        const cardMatch = str.match(/(?:card|card_number)=([A-Za-z0-9-]+)/i);
        const serialMatch = str.match(/(?:serial|serial_number)=([A-Za-z0-9-]+)/i);
        if (cardMatch) {
          return { card: cardMatch[1].trim(), serial: serialMatch ? serialMatch[1].trim() : "" };
        }
      }
    }

    // Check if colon-delimited format: CARD_NO:SERIAL_NO
    if (str.includes(":")) {
      const parts = str.split(":");
      if (parts.length >= 2 && /^[A-Za-z]{3,8}-\d{3,6}$/.test(parts[0].trim())) {
        return { card: parts[0].trim(), serial: parts[1].trim() };
      }
    }

    // Check if raw card number like GERT-0001
    if (/^[A-Za-z]{3,8}-\d{3,6}$/.test(str)) {
      return { card: str, serial: "" };
    }

    return null;
  };

  const isTopCoinCard = (data) => {
    return Boolean(extractTopCoinCardParams(data));
  };

  const extractDistributionToken = (data) => {
    if (!data) return null;
    const str = String(data).trim();
    if (str.startsWith("RTCD-")) {
      return str;
    }
    if (str.includes("token=RTCD-") || str.includes("claim-distribution")) {
      try {
        let urlObj;
        if (str.startsWith("http://") || str.startsWith("https://")) {
          urlObj = new URL(str);
        } else {
          urlObj = new URL(str, "https://topcoinland.com");
        }
        const tok = urlObj.searchParams.get("token");
        if (tok) return tok.trim();
      } catch (e) {
        const match = str.match(/token=(RTCD-[A-Za-z0-9]+)/i);
        if (match) return match[1].trim();
      }
    }
    return null;
  };

  const sendToBackend = async (qrData) => {
    const token = localStorage.getItem("auth_token");
    if (!token) throw new Error("Not authenticated");

    console.log("Sending QR data to backend:", qrData);

    const response = await fetch(`${API_URL}/api/promos/scan`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
      },
      body: JSON.stringify({ qr_data: qrData }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || "Failed to process QR code");
    }

    return data;
  };

  // Handle successful scan - FIXED version for @yudiel/react-qr-scanner
  const handleScan = async (detectedCodes) => {
    // Prevent duplicate processing
    if (!detectedCodes || !scanning || isProcessing.current || loading) {
      return;
    }

    console.log("Detected codes:", detectedCodes);

    // Handle the format from @yudiel/react-qr-scanner
    let qrValue = null;
    
    if (Array.isArray(detectedCodes) && detectedCodes.length > 0) {
      // The library returns an array of detected codes
      const firstCode = detectedCodes[0];
      
      // Try to get the raw value - different possible formats
      if (firstCode.rawValue) {
        qrValue = firstCode.rawValue;
      } else if (firstCode.text) {
        qrValue = firstCode.text;
      } else if (firstCode.data) {
        qrValue = firstCode.data;
      } else if (typeof firstCode === 'string') {
        qrValue = firstCode;
      }
    } else if (detectedCodes && typeof detectedCodes === 'object') {
      // Handle single object
      qrValue = detectedCodes.rawValue || detectedCodes.text || detectedCodes.data;
    } else if (typeof detectedCodes === 'string') {
      qrValue = detectedCodes;
    }

    if (!qrValue) {
      console.warn("Could not extract value from scan result:", detectedCodes);
      return;
    }

    console.log("Extracted QR value:", qrValue);

    // If it's a TopCoin distribution card QR code, redirect directly to Claim Card page in Customer Portal!
    const cardParams = extractTopCoinCardParams(qrValue);
    if (cardParams) {
      isProcessing.current = false;
      const targetUrl = `/customer/claim-card?card=${encodeURIComponent(cardParams.card)}${
        cardParams.serial ? `&serial=${encodeURIComponent(cardParams.serial)}` : ""
      }`;
      navigate(targetUrl);
      return;
    }

    // If it's a Retail TC Distribution QR code, redirect directly to Claim Distribution page!
    const distToken = extractDistributionToken(qrValue);
    if (distToken) {
      isProcessing.current = false;
      navigate(`/customer/claim-distribution?token=${encodeURIComponent(distToken)}`);
      return;
    }

    // Process the scan
    isProcessing.current = true;
    setScanning(false);
    setLoading(true);
    setError(null);

    try {
      const data = await sendToBackend(qrValue);
      setScanResult(data.data);
      setLoading(false);
    } catch (err) {
      console.error("Scan error:", err);
      setError(err.message);
      
      // Reset scanner after error
      setTimeout(() => {
        setScanning(true);
        setLoading(false);
        isProcessing.current = false;
      }, 3000);
    }
  };

  // Handle manual code submission
  const handleManualSubmit = async (e) => {
    e.preventDefault();

    if (!manualCode.trim()) {
      setError("Please enter a promo code");
      return;
    }

    const cardParams = extractTopCoinCardParams(manualCode.trim());
    if (cardParams) {
      isProcessing.current = false;
      setShowManualInput(false);
      const targetUrl = `/customer/claim-card?card=${encodeURIComponent(cardParams.card)}${
        cardParams.serial ? `&serial=${encodeURIComponent(cardParams.serial)}` : ""
      }`;
      navigate(targetUrl);
      return;
    }

    const distToken = extractDistributionToken(manualCode.trim());
    if (distToken) {
      isProcessing.current = false;
      setShowManualInput(false);
      navigate(`/customer/claim-distribution?token=${encodeURIComponent(distToken)}`);
      return;
    }

    setLoading(true);
    setError(null);
    isProcessing.current = true;

    try {
      const data = await sendToBackend(manualCode.trim());
      setScanResult(data.data);
      setShowManualInput(false);
      setManualCode("");
    } catch (err) {
      console.error("Manual entry error:", err);
      setError(err.message);
    } finally {
      setLoading(false);
      isProcessing.current = false;
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // For QR code image upload, you'd need a library like 'jsqr'
    // This is a placeholder
    setError("QR image upload requires additional implementation. Please use camera.");
    
    // Reset file input
    e.target.value = '';
  };

  const resetScanner = () => {
    setScanning(true);
    setError(null);
    setScanResult(null);
    setCameraError(false);
    setShowManualInput(false);
    setManualCode("");
    isProcessing.current = false;
    // Force scanner remount
    setScannerKey((prev) => prev + 1);
  };

  const closeResult = () => {
    setScanResult(null);
    resetScanner();
  };

  const handleScannerError = (error) => {
    console.error("Scanner error:", error);
    
    // Handle camera permission errors
    if (error.name === "NotAllowedError" || error.message?.includes("permission")) {
      setCameraError(true);
      setError("Camera access denied. Please allow camera access.");
    } 
    // Handle camera not available
    else if (error.name === "NotFoundError" || error.message?.includes("not found")) {
      setCameraError(true);
      setError("No camera found.");
    }
    // Handle other errors
    else if (error?.message && !error.message.includes("No MultiFormat")) {
      setError("Camera error: " + (error.message || "Unknown error"));
    }
  };

  const retryCamera = () => {
    setCameraError(false);
    setError(null);
    setScannerKey((prev) => prev + 1);
  };

  const getContainerStyle = () => {
    const role = user?.role || "Customer";
    let background = "";

    switch (role) {
      case "Senior Ads Agent":
      case "Senior Customer Advertising Agent":
        background = "linear-gradient(to bottom, #0d6efd, white)";
        break;
      case "Mini Ads Agent":
      case "Mini Customer Advertising Agent":
        background = "linear-gradient(to bottom, #0000a0, white)";
        break;
      case "Customer":
      default:
        background = "linear-gradient(to bottom, #6f42c1, white)";
        break;
    }

    return {
      display: "flex",
      flexDirection: "column",
      minHeight: "100vh",
      background: background,
      fontFamily: "'Poppins', sans-serif",
    };
  };

  // Calculate responsive scanner dimensions
  const getScannerStyles = () => {
    const containerWidth = scannerDimensions.width || 400;
    const containerHeight = scannerDimensions.height || 400;
    
    // Maintain aspect ratio for scanner
    let videoWidth = containerWidth;
    let videoHeight = containerHeight;
    
    // Ensure minimum dimensions for mobile
    if (window.innerWidth <= 768) {
      videoWidth = Math.min(containerWidth, window.innerWidth - 40);
      videoHeight = videoWidth * 0.75; // 4:3 aspect ratio for mobile
    }
    
    return {
      container: { 
        width: "100%", 
        height: "100%",
        position: "relative",
        overflow: "hidden",
        borderRadius: "12px",
      },
      video: { 
        width: "100%", 
        height: "100%", 
        objectFit: "cover",
        transform: "scale(1.02)", // Slight zoom to eliminate black borders
      }
    };
  };

  return (
    <div className="qr-scanner-page" style={getContainerStyle()}>
      <div className="qr-scanner-container">
        {/* Header */}
        <div className="scanner-header">
          <h1>
            <MdQrCodeScanner className="header-icon" />
            Scan QR Code
          </h1>
          <p>Scan a product QR code to earn TopCoins</p>
        </div>

        {/* Main Content */}
        <div className="scanner-layout">
          {/* Left Column - Scanner */}
          <div className="scanner-main">
            {!scanResult && (
              <>
                {/* Controls */}
                <div className="scanner-controls">
                  {/* Commented out controls as in original */}
                </div>

                {/* Manual Input */}
                {showManualInput && (
                  <form onSubmit={handleManualSubmit} className="manual-input-form">
                    <input
                      type="text"
                      value={manualCode}
                      onChange={(e) => setManualCode(e.target.value)}
                      placeholder="Enter promo code (e.g., 0001-001-0001-MBP16-10-TKOJA)"
                      disabled={loading}
                      className="manual-input"
                      autoFocus
                     maxLength={255} />
                    <button
                      type="submit"
                      disabled={loading || !manualCode.trim()}
                      className="submit-btn"
                    >
                      {loading ? "Processing..." : "Submit"}
                    </button>
                  </form>
                )}

                {/* Scanner Area - Responsive */}
                <div 
                  className="scanner-wrapper responsive-scanner" 
                  ref={scannerContainerRef}
                >
                  {scanning && !cameraError ? (
                    <div className="scanner-container">
                      <Scanner
                        key={scannerKey}
                        onScan={handleScan}
                        onError={handleScannerError}
                        constraints={{
                          facingMode: "environment",
                          width: { min: 640, ideal: 1280, max: 1920 },
                          height: { min: 480, ideal: 720, max: 1080 }
                        }}
                        styles={getScannerStyles()}
                        scanDelay={500} // Add delay between scans to prevent duplicates
                      />
                      
                      {/* Scanner Overlay for better UX */}
                      <div className="scanner-overlay">
                        <div className="scanner-frame"></div>
                        <p className="scanner-hint">Position QR code within frame</p>
                      </div>
                    </div>
                  ) : (
                    <div className="scanner-placeholder">
                      {cameraError ? (
                        <>
                          <MdError className="placeholder-icon error" />
                          <p>Camera not available</p>
                          <div className="placeholder-actions">
                            <button onClick={retryCamera} className="retry-btn">
                              <MdRefresh /> Retry Camera
                            </button>
                           {/*  <button 
                              onClick={() => setShowManualInput(true)} 
                              className="manual-btn"
                            >
                              Use Manual Entry
                            </button>*/}
                          </div>
                        </>
                      ) : (
                        <>
                          <MdCameraAlt className="placeholder-icon" />
                          <p>{loading ? "Processing..." : "Scanner paused"}</p>
                          {!loading && !scanResult && (
                            <button onClick={resetScanner} className="reset-btn">
                              Resume Scanning
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>

                {/* Loading Overlay */}
                {loading && (
                  <div className="loading-overlay">
                    <div className="spinner"></div>
                    <p>Processing QR code...</p>
                  </div>
                )}

                {/* Error Message */}
                {error && !cameraError && (
                  <div className="error-message-qr">
                    <MdError className="error-icon-qr" />
                    <span>{error}</span>
                    <button onClick={() => setError(null)} className="close-btn-qr">
                      <MdClose />
                    </button>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Right Column - Instructions */}
          <div className="scanner-instructions-sidebar">
            <div className="instructions-card">
              <h3>
                <MdInfo /> How to Scan
              </h3>
              <ul>
                <li>Hold your phone steady over the QR code</li>
                <li>Ensure good lighting for better detection</li>
                <li>Keep the QR code within the frame</li>
                <li>Scanner will automatically detect the code</li>
              </ul>
              
              {cameraError && (
                <div className="camera-help">
                  <h4>Camera Issues?</h4>
                  <ul>
                    <li>Check camera permissions in browser settings</li>
                    <li>Make sure no other app is using the camera</li>
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Success Modal */}
        {scanResult && (
          <div className="result-modal">
            <div className="result-content">
              <button onClick={closeResult} className="modal-close">
                <MdClose />
              </button>

              <MdCheckCircle className="success-icon" />
              <h2>Success!</h2>
              <p>TopCoins have been added to your wallet</p>

              <div className="result-details">
                {scanResult.is_topcoin_card ? (
                  <>
                    <div className="detail-row">
                      <span className="detail-label">Card Number:</span>
                      <span className="detail-value code">{scanResult.promo_code}</span>
                    </div>

                    <div className="detail-row">
                      <span className="detail-label">Card Tier:</span>
                      <span className="detail-value" style={{ fontWeight: "bold", color: scanResult.card_color || "#6f42c1" }}>
                        ● {scanResult.card_tier}
                      </span>
                    </div>

                    <div className="detail-row">
                      <span className="detail-label">Retail Shop:</span>
                      <span className="detail-value" style={{ fontWeight: "600" }}>{scanResult.shop_name}</span>
                    </div>

                    <div className="detail-row highlight">
                      <span className="detail-label">TopCoins Earned:</span>
                      <span className="detail-value amount">+{scanResult.topcoin_amount} TopCoin</span>
                    </div>

                    {scanResult.sbc_ref && (
                      <div style={{ marginTop: "12px", fontSize: "11px", color: "#64748b", wordBreak: "break-all", textAlign: "center" }}>
                        Blockchain Ref: {scanResult.sbc_ref}
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div className="detail-row">
                      <span className="detail-label">Promo Code:</span>
                      <span className="detail-value code">{scanResult.promo_code}</span>
                    </div>

                    <div className="detail-row highlight">
                      <span className="detail-label">TopCoins Earned:</span>
                      <span className="detail-value amount">{scanResult.topcoin_amount}</span>
                    </div>
                  </>
                )}

                {scanResult.code_parts && (
                  <div className="code-parts">
                    <h4>Code Breakdown</h4>
                    <div className="parts-grid">
                      <div className="part">
                        <span className="part-label">Supplier</span>
                        <span className="part-value">{scanResult.code_parts.supplier_code}</span>
                      </div>
                      <div className="part">
                        <span className="part-label">Agent</span>
                        <span className="part-value">{scanResult.code_parts.agent_code}</span>
                      </div>
                      <div className="part">
                        <span className="part-label">Retail</span>
                        <span className="part-value">{scanResult.code_parts.retail_code}</span>
                      </div>
                      <div className="part">
                        <span className="part-label">Product</span>
                        <span className="part-value">{scanResult.code_parts.product_code}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="result-actions">
                <button onClick={closeResult} className="scan-again-btn">
                  Scan Another
                </button>
                <button
                  onClick={() => navigate("/customer/wallet")}
                  className="view-wallet-btn"
                >
                  View Wallet
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default QrScanner;